import React from 'react';
import {
  InputBaseComponentProps,
  Autocomplete as MuiAutocomplete,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FieldInfoIcon } from 'src/components/FieldInfoIcon';

export default function Autocomplete({
  options,
  value,
  onChange,
  handleClearClick: _handleClearClick = () => {},
  label,
  placeholder,
  sx,
  multiple,
  errorText,
  freeSolo = false,
  disableClearable = false,
  inputProps = {},
  showInfoIcon = false,
  infoText,
}: {
  options: any[] | undefined;
  value: string | string[] | null;
  onChange: (_value: string | null) => void;
  handleClearClick?: () => void;
  label?: string;
  placeholder?: string;
  sx?: { [key: string]: any };
  multiple?: boolean;
  errorText?: React.ReactNode | null;
  freeSolo?: boolean;
  disableClearable?: boolean;
  inputProps?: InputBaseComponentProps;
  showInfoIcon?: boolean;
  infoText?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const _options = (options || []).map((item: any) => item.value);
  const normalizedValue = (() => {
    if (multiple) {
      if (!Array.isArray(value)) {
        return [];
      }
      if (freeSolo) {
        return value;
      }
      return value.filter((item) => _options.includes(item));
    }

    if (value === '' || value === null || Array.isArray(value)) {
      return null;
    }

    if (freeSolo || _options.includes(value)) {
      return value;
    }

    return null;
  })();
  const isError = errorText !== undefined;
  const helperText = typeof errorText === 'string' ? t(errorText) : undefined;
  const resolvedInfoText =
    infoText ??
    (typeof label === 'string'
      ? `Provide information about ${label}.`
      : 'Provide information for this field.');
  return (
    <MuiAutocomplete
      key={options?.length || 'autocomplete'}
      freeSolo={freeSolo}
      multiple={multiple || false}
      fullWidth
      loading={options === undefined}
      options={_options}
      value={normalizedValue}
      isOptionEqualToValue={(option, selectedValue) => option === selectedValue}
      getOptionLabel={(optionValue) =>
        options?.find((item: any) => item.value === optionValue)?.label || ''
      }
      onChange={(_, newValue) => onChange(newValue || null)}
      disableClearable={disableClearable}
      renderInput={(params) => (
        <TextField
          {...{
            ...params,
            InputProps: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {inputProps?.endAdornment}
                  {showInfoIcon && <FieldInfoIcon title={resolvedInfoText} />}
                  {params.InputProps?.endAdornment}
                </>
              ),
            },
          }}
          label={label}
          placeholder={placeholder}
          error={isError}
          helperText={helperText}
        />
      )}
      sx={sx}
    />
  );
}
